import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Image, Linking, Pressable } from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import { useSelector, useDispatch } from 'react-redux';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { User, HelpCircle, LogOut, Building2, Stethoscope, X } from 'lucide-react';
import { webColors, webBorderRadius, webShadows } from '../../constants/webStyles';
import { useIsMobile } from '../../hooks/useIsMobile';
import { getOverview } from '../../services/profile';
import { logout } from '../../services/auth';
import { setLoggedOut, setIsDoctor } from '../../reducers/auth/user';
import { resetFields } from '../../reducers/profile/profile';
import SupermedicLogo from '../../assets/images/supermedic-logo-web.png';
import SupermedicLogoFacilities from '../../assets/images/supermedic-logo-facilities-web.png';

const WebNavbar = ({ variant = 'default' }) => {
  const navigation = useNavigation();
  const route = useRoute();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const user = useSelector(state => state.user);
  const isDoctor = user?.isDoctor;
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [authIntent, setAuthIntent] = useState('signin'); // 'signin' | 'register'

  // Mobile responsive detection
  const isMobile = useIsMobile();

  // Helper function to manage drawer state and body scroll lock
  const setDrawerState = (isOpen) => {
    setDrawerOpen(isOpen);
    if (typeof document !== 'undefined') {
      document.body.classList.toggle('drawer-open', isOpen);
    }
  };

  // Handle drawer toggle with body scroll lock (uses functional update to prevent race conditions)
  const toggleDrawer = () => {
    setDrawerOpen(prev => {
      const newState = !prev;
      if (typeof document !== 'undefined') {
        document.body.classList.toggle('drawer-open', newState);
      }
      return newState;
    });
  };

  // Close drawer when transitioning from mobile to desktop
  useEffect(() => {
    if (!isMobile && drawerOpen) {
      setDrawerState(false);
    }
  }, [isMobile, drawerOpen]);

  // Cleanup body scroll lock on unmount
  useEffect(() => {
    return () => {
      if (typeof document !== 'undefined') {
        document.body.classList.remove('drawer-open');
      }
    };
  }, []);

  // Close drawer and navigate
  const handleNavigate = (screen) => {
    if (isMobile && drawerOpen) {
      setDrawerState(false);
    }
    setShowProfileDropdown(false);
    navigation.navigate(screen);
  };

  // Logout mutation
  const logoutMutation = useMutation({
    mutationFn: logout,
    onSuccess: () => {
      dispatch(resetFields());
      dispatch(setLoggedOut());
      queryClient.clear();
      navigation.replace('AuthScreen');
    },
    onError: () => {
      // Even on error, log out locally
      dispatch(resetFields());
      dispatch(setLoggedOut());
      queryClient.clear();
      navigation.replace('AuthScreen');
    },
  });

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to log out?')) {
      logoutMutation.mutate({});
    }
  };

  // Open the role-chooser modal for a given intent ('signin' | 'register')
  const openAuthModal = (intent) => {
    setAuthIntent(intent);
    setShowSignInModal(true);
  };

  // Handle role selection from the modal, routing by intent
  const handleAuthOption = (isDoctorChoice) => {
    dispatch(setIsDoctor(isDoctorChoice));
    setShowSignInModal(false);
    if (isMobile && drawerOpen) {
      setDrawerState(false);
    }
    if (authIntent === 'register') {
      // Registration flows are top-level routes (see SharedStack in navigation.js)
      navigation.navigate(isDoctorChoice ? 'DoctorAuth' : 'FacilityAuth');
    } else {
      navigation.navigate('AuthScreen', { screen: 'LoginScreen' });
    }
  };

  // Sign In Modal (rendered inline to avoid remount issues with inner components)
  const signInModal = showSignInModal ? (
    <>
      <Pressable
        style={styles.modalOverlay}
        onPress={() => setShowSignInModal(false)}
      />
      <View style={styles.modalContainer}>
        <View style={styles.modalContent}>
          {/* Close button */}
          <TouchableOpacity
            style={styles.modalCloseBtn}
            onPress={() => setShowSignInModal(false)}
          >
            <X size={20} color={webColors.textSecondary} />
          </TouchableOpacity>

          {/* Header */}
          <Text style={styles.modalTitle}>
            {authIntent === 'register' ? 'Create an account as' : 'Sign in as'}
          </Text>
          <Text style={styles.modalSubtitle}>
            {authIntent === 'register'
              ? "Choose how you'd like to get started"
              : "Choose how you'd like to continue"}
          </Text>

          {/* Options */}
          <View style={styles.modalOptions}>
            {/* Employer Option */}
            <TouchableOpacity
              style={styles.modalOptionCard}
              onPress={() => handleAuthOption(false)}
              activeOpacity={0.7}
            >
              <View style={styles.modalOptionIconWrapper}>
                <Building2 size={28} color={webColors.primary} />
              </View>
              <Text style={styles.modalOptionLabel}>For Employers</Text>
              <Text style={styles.modalOptionAppName}>Supermedic Hire</Text>
              <Text style={styles.modalOptionDesc}>
                Built for medical institutions and healthcare employers.{'\n'}Post jobs. Hire qualified professionals faster.
              </Text>
            </TouchableOpacity>

            {/* Professional Option */}
            <TouchableOpacity
              style={styles.modalOptionCard}
              onPress={() => handleAuthOption(true)}
              activeOpacity={0.7}
            >
              <View style={styles.modalOptionIconWrapper}>
                <Stethoscope size={28} color={webColors.primary} />
              </View>
              <Text style={styles.modalOptionLabel}>For Professionals</Text>
              <Text style={styles.modalOptionAppName}>Supermedic</Text>
              <Text style={styles.modalOptionDesc}>
                Built for medical and non-medical healthcare professionals.{'\n'}Find jobs. Apply easily. Grow your career.
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </>
  ) : null;

  // Fetch overview data for user info (only for logged-in variant)
  const { data: overviewQuery } = useQuery({
    queryKey: ['overview'],
    queryFn: getOverview,
    enabled: variant !== 'auth',
  });
  const profileInfo = overviewQuery?.data?.data;

  // Build profile picture URI from base64 (same pattern used across the app, e.g. Home.js / ProfileDoctors)
  const profileImageUri = profileInfo?.profile_image
    ? 'data:image/jpg;base64,' + decodeURIComponent(profileInfo.profile_image)
    : null;

  // Compute display name based on user type
  const displayName = isDoctor
    ? `${profileInfo?.first_name || ''}`.trim() || 'User'
    : profileInfo?.clinic_name || 'User';

  const displayRole = isDoctor
    ? profileInfo?.professions?.[0]?.name || 'Healthcare Professional'
    : profileInfo?.facility?.name || 'Facility Admin';

  // Get initials for avatar fallback
  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Check if a nav item is active based on current route
  const isActiveRoute = (screen) => {
    return route.name === screen;
  };

  // Auth variant navigation items (public pages)
  const authNavItems = [
    { label: 'Jobs', screen: 'PublicJobs' },
    { label: 'Professional', screen: 'LandingScreen' },
    { label: 'Employer', screen: 'Employer' },
    { label: 'Reach Us', screen: 'ReachUs' },
  ];

  // Logged-in navigation items based on user role
  const loggedInNavItems = isDoctor ? [
    { label: 'Home', screen: 'Home' },
    { label: 'Jobs', screen: 'Jobs' },
    { label: 'Activity', screen: 'Inbox' },
    { label: 'History', screen: 'JobHistory' },
    { label: 'Account', screen: 'ProfileDoctor' },
  ] : [
    { label: 'Home', screen: 'Home' },
    { label: 'Jobs', screen: 'MyJobs' },
    { label: 'Activity', screen: 'Inbox' },
    { label: 'History', screen: 'JobHistory' },
    { label: 'Account', screen: 'ProfileFacility' },
  ];

  const navItems = variant === 'auth' ? authNavItems : loggedInNavItems;

  // Logo component - shared between variants
  // Auth variant uses default logo, logged-in uses role-based logo
  const logoSource = variant === 'auth'
    ? SupermedicLogo
    : (isDoctor ? SupermedicLogo : SupermedicLogoFacilities);

  const Logo = () => (
    <TouchableOpacity
      style={styles.logo}
      onPress={() => navigation.navigate(variant === 'auth' ? 'PublicJobs' : 'Home')}
    >
      <Image
        source={logoSource}
        style={styles.logoImage}
        resizeMode="contain"
      />
    </TouchableOpacity>
  );

  // Navigation component - shared between variants
  const Navigation = () => (
    <View style={styles.nav}>
      {navItems.map((item, index) => {
        const isActive = isActiveRoute(item.screen);
        return (
          <TouchableOpacity
            key={index}
            style={[styles.navItem, isActive && styles.navItemActive]}
            onPress={() => navigation.navigate(item.screen)}
          >
            <Text style={[styles.navItemText, isActive && styles.navItemTextActive]}>
              {item.label}
            </Text>
          </TouchableOpacity>
        );
      })}
    </View>
  );

  // Hamburger Icon for mobile
  const HamburgerIcon = () => (
    <TouchableOpacity
      onPress={toggleDrawer}
      style={styles.hamburgerButton}
      accessibilityLabel={drawerOpen ? 'Close menu' : 'Open menu'}
    >
      <View style={styles.hamburgerIconContainer}>
        {drawerOpen ? (
          <Text style={styles.hamburgerCloseText}>✕</Text>
        ) : (
          <>
            <View style={styles.hamburgerLine} />
            <View style={styles.hamburgerLine} />
            <View style={styles.hamburgerLine} />
          </>
        )}
      </View>
    </TouchableOpacity>
  );

  // Mobile Drawer Menu
  const MobileDrawer = () => (
    <>
      {/* Overlay backdrop - only rendered when drawer is open */}
      {drawerOpen && (
        <Pressable
          style={styles.mobileDrawerOverlay}
          onPress={toggleDrawer}
        />
      )}

      {/* Drawer */}
      <View style={[styles.mobileDrawer, drawerOpen && styles.mobileDrawerOpen]}>
        {/* Drawer Header */}
        <View style={styles.drawerHeader}>
          <Image
            source={logoSource}
            style={styles.drawerLogo}
            resizeMode="contain"
          />
          <TouchableOpacity onPress={toggleDrawer} style={styles.drawerClose}>
            <Text style={styles.drawerCloseText}>×</Text>
          </TouchableOpacity>
        </View>

        {/* User Profile Section (for logged-in variant) */}
        {variant !== 'auth' && (
          <View style={styles.drawerUserSection}>
            <View style={styles.drawerAvatar}>
              {profileImageUri ? (
                <Image source={{ uri: profileImageUri }} style={styles.avatarImage} resizeMode="cover" />
              ) : (
                <Text style={styles.drawerAvatarText}>{getInitials(displayName)}</Text>
              )}
            </View>
            <Text style={styles.drawerUserName}>{displayName}</Text>
            <Text style={styles.drawerUserRole} numberOfLines={2}>{displayRole}</Text>
          </View>
        )}

        {/* Navigation Links */}
        <View style={styles.drawerNav}>
          {navItems.map((item, index) => {
            const isActive = isActiveRoute(item.screen);
            return (
              <TouchableOpacity
                key={index}
                style={[styles.drawerNavItem, isActive && styles.drawerNavItemActive]}
                onPress={() => handleNavigate(item.screen)}
              >
                <Text style={[styles.drawerNavText, isActive && styles.drawerNavTextActive]}>
                  {item.label}
                </Text>
              </TouchableOpacity>
            );
          })}
        </View>

        {/* Auth Buttons (for auth variant) */}
        {variant === 'auth' && (
          <View style={styles.drawerAuthButtons}>
            <TouchableOpacity
              style={styles.drawerSignInBtn}
              onPress={() => {
                toggleDrawer();
                openAuthModal('signin');
              }}
            >
              <Text style={styles.drawerSignInBtnText}>Sign In</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.drawerCreateAccountBtn}
              onPress={() => {
                toggleDrawer();
                openAuthModal('register');
              }}
            >
              <Text style={styles.drawerCreateAccountBtnText}>Create an account</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.drawerEmployerBtn}
              onPress={() => {
                toggleDrawer();
                Linking.openURL(
                  isDoctor
                    ? 'https://employer.hiresupermedic.com/login'
                    : 'https://hiresupermedic.com/login'
                );
              }}
            >
              <Text style={styles.drawerEmployerBtnText}>
                {isDoctor ? 'Employer - Post a Job' : 'Professional - Find Jobs'}
              </Text>
            </TouchableOpacity>
          </View>
        )}

        {/* Logged-in Actions */}
        {variant !== 'auth' && (
          <View style={styles.drawerActions}>
            <TouchableOpacity
              style={styles.drawerActionItem}
              onPress={() => handleNavigate(isDoctor ? 'ProfileDoctor' : 'ProfileFacility')}
            >
              <User size={20} color={webColors.textSecondary} />
              <Text style={styles.drawerActionText}>My Profile</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.drawerActionItem}
              onPress={() => handleNavigate('HelpSupportScreen')}
            >
              <HelpCircle size={20} color={webColors.textSecondary} />
              <Text style={styles.drawerActionText}>Help & Support</Text>
            </TouchableOpacity>
            <View style={styles.drawerDivider} />
            <TouchableOpacity
              style={styles.drawerActionItem}
              onPress={() => {
                toggleDrawer();
                handleLogout();
              }}
            >
              <LogOut size={20} color={webColors.error} />
              <Text style={styles.drawerLogoutText}>Log Out</Text>
            </TouchableOpacity>
          </View>
        )}
      </View>
    </>
  );

  // Auth variant - pre-login header
  if (variant === 'auth') {
    return (
      <>
        <View style={[styles.header, isMobile && styles.headerMobile, isMobile && styles.headerSticky]}>
          <Logo />
          {!isMobile && <Navigation />}
          {isMobile ? (
            <HamburgerIcon />
          ) : (
            <View style={styles.authButtons}>
              <TouchableOpacity
                style={styles.signInBtn}
                onPress={() => openAuthModal('signin')}
              >
                <Text style={styles.signInBtnText}>Sign In</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.createAccountBtn}
                onPress={() => openAuthModal('register')}
              >
                <Text style={styles.createAccountBtnText}>Create an account</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.employerBtn}
                onPress={() => Linking.openURL(
                  isDoctor
                    ? 'https://employer.hiresupermedic.com/login'
                    : 'https://hiresupermedic.com/login'
                )}
              >
                <Text style={styles.employerBtnText}>{isDoctor ? 'Employer' : 'Professional'}</Text>
                <View style={styles.employerBtnDivider} />
                <Text style={styles.employerBtnText}>{isDoctor ? 'Post a Job' : 'Find Jobs'}</Text>
              </TouchableOpacity>
            </View>
          )}
        </View>
        {isMobile && <MobileDrawer />}
        {signInModal}
      </>
    );
  }

  // Default variant - logged-in header
  return (
    <>
      <View style={[styles.header, isMobile && styles.headerMobile, isMobile && styles.headerSticky]}>
        <Logo />
        {!isMobile && <Navigation />}
        {isMobile ? (
          <HamburgerIcon />
        ) : (
          <View style={styles.rightSection}>
            {/* User Profile with Dropdown */}
            <View style={styles.profileDropdownWrapper}>
              <TouchableOpacity
                style={styles.userProfile}
                onPress={() => setShowProfileDropdown(!showProfileDropdown)}
              >
                <View style={styles.userAvatar}>
                  {profileImageUri ? (
                    <Image source={{ uri: profileImageUri }} style={styles.avatarImage} resizeMode="cover" />
                  ) : (
                    <Text style={styles.userInitials}>{getInitials(displayName)}</Text>
                  )}
                </View>
                <View style={styles.userInfo}>
                  <Text style={styles.userName}>{displayName}</Text>
                  <Text style={styles.userRole} numberOfLines={1} ellipsizeMode="tail">{displayRole?.length > 20 ? displayRole.substring(0, 20) + '...' : displayRole}</Text>
                </View>
                <View style={[styles.dropdownArrow, showProfileDropdown && styles.dropdownArrowOpen]}>
                  <Text style={styles.dropdownArrowText}>▼</Text>
                </View>
              </TouchableOpacity>

              {/* Dropdown Menu */}
              {showProfileDropdown && (
                <>
                  {/* Backdrop to close dropdown on click outside */}
                  <Pressable
                    style={styles.dropdownBackdrop}
                    onPress={() => setShowProfileDropdown(false)}
                  />
                  <View style={styles.profileDropdownMenu}>
                    {/* User Info Header */}
                    <View style={styles.dropdownHeader}>
                      <View style={styles.dropdownAvatar}>
                        {profileImageUri ? (
                          <Image source={{ uri: profileImageUri }} style={styles.avatarImage} resizeMode="cover" />
                        ) : (
                          <Text style={styles.dropdownAvatarText}>{getInitials(displayName)}</Text>
                        )}
                      </View>
                      <Text style={styles.dropdownUserName}>{displayName}</Text>
                      <Text style={styles.dropdownUserRole}>{displayRole}</Text>
                    </View>

                    {/* Divider */}
                    <View style={styles.dropdownDivider} />

                    {/* Menu Items */}
                    <TouchableOpacity
                      style={styles.dropdownMenuItem}
                      onPress={() => {
                        setShowProfileDropdown(false);
                        navigation.navigate(isDoctor ? 'ProfileDoctor' : 'ProfileFacility');
                      }}
                    >
                      <View style={styles.dropdownMenuIcon}>
                        <User size={18} color={webColors.textSecondary} />
                      </View>
                      <Text style={styles.dropdownMenuText}>My Profile</Text>
                    </TouchableOpacity>

                    <TouchableOpacity
                      style={styles.dropdownMenuItem}
                      onPress={() => {
                        setShowProfileDropdown(false);
                        navigation.navigate('HelpSupportScreen');
                      }}
                    >
                      <View style={styles.dropdownMenuIcon}>
                        <HelpCircle size={18} color={webColors.error} />
                      </View>
                      <Text style={styles.dropdownMenuText}>Help & Support</Text>
                    </TouchableOpacity>

                    {/* Divider */}
                    <View style={styles.dropdownDivider} />

                    {/* Logout */}
                    <TouchableOpacity
                      style={styles.dropdownMenuItem}
                      onPress={() => {
                        setShowProfileDropdown(false);
                        handleLogout();
                      }}
                    >
                      <View style={styles.dropdownMenuIcon}>
                        <LogOut size={18} color={webColors.error} />
                      </View>
                      <Text style={styles.dropdownMenuTextLogout}>Log Out</Text>
                    </TouchableOpacity>
                  </View>
                </>
              )}
            </View>
          </View>
        )}
      </View>
      {isMobile && <MobileDrawer />}
    </>
  );
};

const styles = StyleSheet.create({
  // Header container - Option 3 style
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    paddingHorizontal: 32,
    backgroundColor: webColors.cardBackground,
    borderBottomWidth: 1,
    borderBottomColor: webColors.border,
    position: 'relative',
    zIndex: 9999,
    overflow: 'visible',
  },

  // Logo styles
  logo: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  logoImage: {
    width: 160,
    height: 40,
  },

  // Navigation styles - pill nav
  nav: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  navItem: {
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 50,
  },
  navItemActive: {
    backgroundColor: webColors.primaryLight,
  },
  navItemText: {
    fontSize: 14,
    fontWeight: '500',
    color: webColors.textSecondary,
  },
  navItemTextActive: {
    color: webColors.primary,
  },

  // Auth buttons styles
  authButtons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  signInBtn: {
    paddingVertical: 12,
    paddingHorizontal: 28,
    borderRadius: 50,
    borderWidth: 1.5,
    borderColor: webColors.border,
    backgroundColor: 'transparent',
  },
  signInBtnText: {
    fontSize: 14,
    fontWeight: '500',
    color: webColors.textPrimary,
  },
  employerBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 50,
    backgroundColor: webColors.textPrimary,
  },
  employerBtnText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#ffffff',
  },
  employerBtnDivider: {
    width: 1,
    height: 14,
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
    marginHorizontal: 12,
  },
  createAccountBtn: {
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 50,
    backgroundColor: webColors.primary,
    ...webShadows.button,
  },
  createAccountBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#ffffff',
  },

  // Right section for logged-in users
  rightSection: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    overflow: 'visible',
  },
  postJobBtn: {
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 50,
    backgroundColor: webColors.primary,
  },
  postJobBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#ffffff',
  },

  // User profile styles
  userProfile: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: webBorderRadius.medium,
    backgroundColor: webColors.backgroundAlt,
  },
  userAvatar: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: webColors.primary,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  avatarImage: {
    width: '100%',
    height: '100%',
  },
  userInitials: {
    fontSize: 14,
    fontWeight: '600',
    color: '#ffffff',
  },
  userInfo: {
    flexDirection: 'column',
  },
  userName: {
    fontSize: 14,
    fontWeight: '600',
    color: webColors.textPrimary,
  },
  userRole: {
    fontSize: 12,
    color: webColors.textSecondary,
  },

  // Profile Dropdown styles
  profileDropdownWrapper: {
    position: 'relative',
    zIndex: 9999,
  },
  dropdownArrow: {
    marginLeft: 4,
    transitionDuration: '200ms',
    transitionProperty: 'transform',
  },
  dropdownArrowOpen: {
    transform: [{ rotate: '180deg' }],
  },
  dropdownArrowText: {
    fontSize: 10,
    color: webColors.textMuted,
  },
  dropdownBackdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 9998,
  },
  profileDropdownMenu: {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: 12,
    backgroundColor: webColors.cardBackground,
    borderRadius: 16,
    minWidth: 240,
    ...webShadows.modal,
    borderWidth: 1,
    borderColor: webColors.border,
    zIndex: 9999,
    overflow: 'hidden',
  },
  dropdownHeader: {
    alignItems: 'center',
    paddingVertical: 24,
    paddingHorizontal: 20,
  },
  dropdownAvatar: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: webColors.primary,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
    overflow: 'hidden',
  },
  dropdownAvatarText: {
    fontSize: 28,
    fontWeight: '600',
    color: '#ffffff',
  },
  dropdownUserName: {
    fontSize: 18,
    fontWeight: '700',
    color: webColors.textPrimary,
    marginBottom: 4,
  },
  dropdownUserRole: {
    fontSize: 14,
    color: webColors.textSecondary,
    textAlign: 'center',
  },
  dropdownDivider: {
    height: 1,
    backgroundColor: webColors.borderLight,
    marginHorizontal: 16,
  },
  dropdownMenuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 14,
    paddingHorizontal: 20,
  },
  dropdownMenuIcon: {
    width: 24,
    marginRight: 14,
    alignItems: 'center',
  },
  dropdownMenuText: {
    fontSize: 15,
    color: webColors.textPrimary,
    fontWeight: '500',
  },
  dropdownMenuTextLogout: {
    fontSize: 15,
    color: webColors.error,
    fontWeight: '500',
  },

  // ==================== MOBILE RESPONSIVE STYLES ====================

  // Mobile header adjustments
  headerMobile: {
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  headerSticky: {
    position: 'sticky',
    top: 0,
    zIndex: 1000,
    ...webShadows.card,
  },

  // Hamburger button and icon
  hamburgerButton: {
    padding: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  hamburgerIconContainer: {
    width: 24,
    height: 18,
    justifyContent: 'space-between',
  },
  hamburgerLine: {
    width: 24,
    height: 2,
    backgroundColor: webColors.textPrimary,
    borderRadius: 1,
  },
  hamburgerCloseText: {
    fontSize: 22,
    color: webColors.textPrimary,
    fontWeight: '300',
    lineHeight: 22,
    textAlign: 'center',
  },

  // Mobile drawer - positioned off-screen by default
  mobileDrawer: {
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    width: 280,
    maxWidth: '85%',
    backgroundColor: webColors.cardBackground,
    ...webShadows.modal,
    zIndex: 10001,
    overflowY: 'auto',
    transform: [{ translateX: 280 }],
  },
  mobileDrawerOpen: {
    transform: [{ translateX: 0 }],
  },
  mobileDrawerOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 10000,
  },

  // Drawer styles
  drawerHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: webColors.borderLight,
  },
  drawerLogo: {
    width: 120,
    height: 30,
  },
  drawerClose: {
    padding: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  drawerCloseText: {
    fontSize: 28,
    color: webColors.textSecondary,
    fontWeight: '300',
    lineHeight: 28,
  },

  // Drawer user section
  drawerUserSection: {
    alignItems: 'center',
    paddingVertical: 24,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
    borderBottomColor: webColors.borderLight,
  },
  drawerAvatar: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: webColors.primary,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
    overflow: 'hidden',
  },
  drawerAvatarText: {
    fontSize: 22,
    fontWeight: '600',
    color: '#ffffff',
  },
  drawerUserName: {
    fontSize: 16,
    fontWeight: '600',
    color: webColors.textPrimary,
    marginBottom: 4,
    textAlign: 'center',
  },
  drawerUserRole: {
    fontSize: 13,
    color: webColors.textSecondary,
    textAlign: 'center',
    paddingHorizontal: 10,
  },

  // Drawer navigation
  drawerNav: {
    paddingVertical: 8,
  },
  drawerNavItem: {
    paddingVertical: 16,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
    borderBottomColor: webColors.borderLight,
  },
  drawerNavItemActive: {
    backgroundColor: webColors.primaryLight,
    borderLeftWidth: 4,
    borderLeftColor: webColors.primary,
  },
  drawerNavText: {
    fontSize: 16,
    fontWeight: '500',
    color: webColors.textSecondary,
  },
  drawerNavTextActive: {
    color: webColors.primary,
    fontWeight: '600',
  },

  // Drawer auth buttons
  drawerAuthButtons: {
    paddingHorizontal: 20,
    paddingVertical: 24,
    gap: 12,
  },
  drawerSignInBtn: {
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: 50,
    borderWidth: 1.5,
    borderColor: webColors.border,
    backgroundColor: 'transparent',
    alignItems: 'center',
  },
  drawerSignInBtnText: {
    fontSize: 15,
    fontWeight: '600',
    color: webColors.textPrimary,
  },
  drawerEmployerBtn: {
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: 50,
    backgroundColor: webColors.textPrimary,
    alignItems: 'center',
  },
  drawerEmployerBtnText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#ffffff',
  },
  drawerCreateAccountBtn: {
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: 50,
    backgroundColor: webColors.primary,
    alignItems: 'center',
  },
  drawerCreateAccountBtnText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#ffffff',
  },

  // Drawer actions
  drawerActions: {
    paddingVertical: 12,
    borderTopWidth: 1,
    borderTopColor: webColors.borderLight,
    marginTop: 'auto',
  },
  drawerActionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 16,
    paddingHorizontal: 20,
    gap: 14,
  },
  drawerActionText: {
    fontSize: 15,
    fontWeight: '500',
    color: webColors.textPrimary,
  },
  drawerDivider: {
    height: 1,
    backgroundColor: webColors.borderLight,
    marginHorizontal: 20,
    marginVertical: 8,
  },
  drawerLogoutText: {
    fontSize: 15,
    fontWeight: '600',
    color: webColors.error,
  },

  // ==================== SIGN IN MODAL STYLES ====================
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 20000,
  },
  modalContainer: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 20001,
    pointerEvents: 'box-none',
  },
  modalContent: {
    backgroundColor: webColors.cardBackground,
    borderRadius: 20,
    padding: 32,
    width: '90%',
    maxWidth: 520,
    ...webShadows.modal,
    pointerEvents: 'auto',
  },
  modalCloseBtn: {
    position: 'absolute',
    top: 16,
    right: 16,
    padding: 8,
    zIndex: 1,
  },
  modalTitle: {
    fontSize: 22,
    fontWeight: '700',
    color: webColors.textPrimary,
    textAlign: 'center',
    marginBottom: 4,
  },
  modalSubtitle: {
    fontSize: 14,
    color: webColors.textSecondary,
    textAlign: 'center',
    marginBottom: 24,
  },
  modalOptions: {
    flexDirection: 'column',
    gap: 14,
  },
  modalOptionCard: {
    borderWidth: 1.5,
    borderColor: webColors.border,
    borderRadius: 16,
    padding: 24,
    alignItems: 'center',
  },
  modalOptionIconWrapper: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: webColors.primaryLight,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 14,
  },
  modalOptionLabel: {
    fontSize: 16,
    fontWeight: '700',
    color: webColors.textPrimary,
    marginBottom: 2,
  },
  modalOptionAppName: {
    fontSize: 13,
    fontWeight: '600',
    color: webColors.primary,
    marginBottom: 8,
  },
  modalOptionDesc: {
    fontSize: 13,
    color: webColors.textSecondary,
    textAlign: 'center',
    lineHeight: 20,
  },
});

export default WebNavbar;
